import { motion } from "framer-motion";
import { Award, Globe2, Lock, GraduationCap } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export default function About() {
  return (
    <div className="pt-40">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16,1,0.3,1] }} className="max-w-3xl">
          <div className="overline mb-6">The Firm</div>
          <h1 className="font-display font-light text-5xl lg:text-6xl leading-[1.05]">A small firm, deliberately.</h1>
          <p className="text-zinc-400 text-lg mt-8 leading-relaxed">
            Tactis Expert Advisory &amp; Mediation is a specialist practice combining chartered financial expertise with accredited mediation. Every instruction is handled personally, discreetly and with the seniority the matter demands.
          </p>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#141414] to-[#0a0a0a] p-10">
              <div className="font-dash text-3xl">The Practitioner</div>
              <div className="text-zinc-400 mt-2 text-sm tracking-wide">Chartered Certified Accountant &amp; International Mediator</div>
              <p className="text-zinc-400 text-sm leading-relaxed mt-6">
                Over 30 years' experience across finance, operations, people and executive leadership in multiple industries — involved in numerous sales and acquisitions, high-net-worth divorce cases, and thoroughly familiar with the boardroom.
              </p>
              <div className="hairline my-8" />
              <div className="space-y-5">
                {[
                  { icon: Award, t: "Chartered Certified Accountant", d: "Decades of financial and forensic experience." },
                  { icon: Globe2, t: "International Mediator", d: "Accredited across commercial and civil disputes." },
                  { icon: Lock, t: "Absolute discretion", d: "Confidentiality at every stage of every matter." },
                ].map((c, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <c.icon size={22} strokeWidth={1} className="text-zinc-300 mt-1 shrink-0" />
                    <div><div className="text-white">{c.t}</div><div className="text-zinc-500 text-sm">{c.d}</div></div>
                  </div>
                ))}
              </div>
              <div className="hairline my-8" />
              <div className="flex items-center gap-2 overline mb-4"><GraduationCap size={15} strokeWidth={1.5} className="text-zinc-300" /> Training &amp; Qualifications</div>
              <div className="space-y-3">
                {[
                  { a: "ACCA", t: "Association of Chartered Certified Accountants" },
                  { a: "CMI", t: "Chartered Management Institute" },
                  { a: "IMI", t: "International Mediation Institute" },
                  { a: "VSI", t: "VSI Executive Education — MBA-level, CEO of a sports organisation" },
                ].map((q, i) => (
                  <div key={i} className="flex items-baseline gap-3">
                    <span className="text-sm text-white font-medium tracking-wide shrink-0 w-12">{q.a}</span>
                    <span className="text-sm text-zinc-500 leading-snug">{q.t}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="overline mb-4">Approach</div>
            <h2 className="font-display font-light text-3xl lg:text-4xl leading-tight">Financial rigour, impartial judgement.</h2>
            <div className="space-y-5 mt-8 text-zinc-400 leading-relaxed">
              <p>Disputes rarely turn on the law alone — they turn on the numbers beneath it. Tactis brings the financial fluency of a chartered accountant to the strategy of litigation, and the neutrality of an accredited mediator to the search for resolution.</p>
              <p>For lawyers and counsel, that means a shadow expert who can test the other side's figures, quantify exposure and strengthen your instructions. For high-net-worth individuals, it means clarity and protection when the stakes are personal.</p>
              <p>For parties seeking to avoid the cost and uncertainty of trial, it means an impartial mediator who understands the commercial reality — facilitating and supporting both parties in generating options to reach a balanced agreement.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
